<script setup lang="ts">
import { useStudyStore } from '@/stores/study';
import { useSurgeonDashboardStore } from '@/stores/surgeonDashboard';
import { computed, onBeforeUnmount, ref, watch } from 'vue';

const studyStore = useStudyStore();
const surgeonStore = useSurgeonDashboardStore();
const reportUrl = 'https://rawalo01.dmz.hpc.mssm.edu/pump-qc/pump-report?clear-site-data=true&hide_back=true';
const reportUrlWithNoCache = ref('');
const iframeRef = ref<HTMLIFrameElement | null>(null);

const IFRAME_LOAD_MAX_WAIT_MS = 20000;
const IFRAME_STATUS_CHECK_INTERVAL_MS = 2000;
const IFRAME_CROSS_ORIGIN_READY_GRACE_MS = 3500;

const iframeLoaded = ref(false);
const iframeLoadStart = ref(0);
const showQcReportError = computed(() => !!surgeonStore.qcReportErrorMessage && !surgeonStore.isQcReportTokenLoading.isLoading);
const showLoader = computed(
  () => surgeonStore.isQcReportTokenLoading.isLoading || (!!reportUrlWithNoCache.value && !iframeLoaded.value)
);

let iframeLoadTimeout: ReturnType<typeof setTimeout> | null = null;
let iframeStatusInterval: ReturnType<typeof setInterval> | null = null;

const clearIframeTimeout = () => {
  if (iframeLoadTimeout) {
    clearTimeout(iframeLoadTimeout);
    iframeLoadTimeout = null;
  }
};

const clearIframeStatusInterval = () => {
  if (iframeStatusInterval) {
    clearInterval(iframeStatusInterval);
    iframeStatusInterval = null;
  }
};

const markIframeAsLoaded = () => {
  iframeLoaded.value = true;
  clearIframeTimeout();
  clearIframeStatusInterval();
};

const detectLoadedIframeContent = () => {
  const iframeEl = iframeRef.value;
  if (!iframeEl || !iframeEl.src || iframeEl.src === 'about:blank') {
    return false;
  }

  try {
    const iframeDocument = iframeEl.contentDocument;
    if (iframeDocument?.readyState === 'complete') {
      return true;
    }

    const iframeHref = iframeEl.contentWindow?.location?.href;
    if (iframeHref && iframeHref !== 'about:blank') {
      return true;
    }
  } catch {
    // Cross-origin frame access throws. If it has had enough time to navigate,
    // treat it as loaded so the overlay doesn't get stuck on top of visible content.
    return Date.now() - iframeLoadStart.value >= IFRAME_CROSS_ORIGIN_READY_GRACE_MS;
  }

  return false;
};

const startIframeMonitoring = () => {
  clearIframeStatusInterval();
  clearIframeTimeout();

  iframeLoadTimeout = setTimeout(() => {
    markIframeAsLoaded();
  }, IFRAME_LOAD_MAX_WAIT_MS);

  iframeStatusInterval = setInterval(() => {
    if (iframeLoaded.value) {
      clearIframeStatusInterval();
      return;
    }

    if (detectLoadedIframeContent()) {
      markIframeAsLoaded();
    }
  }, IFRAME_STATUS_CHECK_INTERVAL_MS);
};

const onIframeLoad = () => {
  markIframeAsLoaded();
};

const onIframeError = () => {
  // Keep UI usable even when load/error events behave inconsistently.
  markIframeAsLoaded();
};

const refreshReportUrl = () => {
  const token = surgeonStore.qcReportToken;
  if (!token) {
    reportUrlWithNoCache.value = '';
    iframeLoaded.value = false;
    clearIframeTimeout();
    clearIframeStatusInterval();
    return;
  }

  const noCacheUrl = new URL(reportUrl);
  noCacheUrl.searchParams.set('_ts', `${Date.now()}`);
  noCacheUrl.searchParams.set('_r', Math.random().toString(36).slice(2));
  noCacheUrl.searchParams.set('token', token);
  reportUrlWithNoCache.value = noCacheUrl.toString();

  iframeLoaded.value = false;
  iframeLoadStart.value = Date.now();
  startIframeMonitoring();
};

watch(
  () => studyStore.selectedStudy?.identifier,
  async studyId => {
    if (!studyId) {
      surgeonStore.clearPumpQcReportsToken();
      refreshReportUrl();
      return;
    }
    await surgeonStore.getPumpQcReportsToken(studyId);
    refreshReportUrl();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  clearIframeTimeout();
  clearIframeStatusInterval();
});

</script>

<template>
  <div class="pump-qc-report-page">
    <div class="iframe-loader" v-if="showLoader">
      <div class="loader-spinner"></div>
    </div>

    <div class="entry-closed-error" v-if="showQcReportError">
      <div class="entry-closed-message">
        <FontAwesomeIcon :icon="['far', 'file-lock']" class="closed-icon" />
        <p>{{ surgeonStore.qcReportErrorMessage }}</p>
      </div>
    </div>

    <iframe class="pump-qc-iframe"
      v-if="reportUrlWithNoCache && !showQcReportError"
      :src="reportUrlWithNoCache"
      :key="reportUrlWithNoCache"
      title="PUMP QC Report"
      sandbox="allow-same-origin allow-popups allow-scripts allow-forms allow-downloads"
      referrerpolicy="no-referrer"
      ref="iframeRef"
      @load="onIframeLoad"
      @error="onIframeError"
      loading="eager" />
  </div>
</template>

<style lang="scss" scoped>
.pump-qc-report-page {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  overflow: hidden;
}

.pump-qc-iframe {
  width: 100%;
  height: 100%;
  border: 0;
}

.entry-closed-error {
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.entry-closed-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 600px;
  text-align: center;
  padding: 40px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 0 8px 2px rgba(0, 0, 0, 0.15);

  .closed-icon {
    font-size: 48px;
    color: #6c757d;
  }

  p {
    font-size: 18px;
    color: #354a4c;
    line-height: 1.6;
    margin: 0;
  }
}

.iframe-loader {
  position: absolute;
  inset: 0;
  z-index: 25;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
}

.loader-spinner {
  width: 46px;
  height: 46px;
  border: 4px solid #d1d5db;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.fallback-card {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 30;
  width: min(720px, calc(100% - 24px));
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);
  padding: 20px;

  h3 {
    margin: 0 0 8px;
    font-size: 18px;
  }

  p {
    margin: 0 0 14px;
    color: #374151;
  }
}

.open-report-button {
  display: inline-block;
  margin-bottom: 14px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #111827;
  color: #fff;
  text-decoration: none;
  font-weight: 600;
}

.open-report-button:hover {
  background: #1f2937;
}

.credentials-overlay {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  width: min(680px, calc(100% - 24px));
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  padding: 12px 14px;
}

.credentials-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }
}

.credential-row {
  display: grid;
  grid-template-columns: 86px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
}

.credential-label {
  font-weight: 600;
  text-transform: lowercase;
}

.credential-value {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  background: #f6f7f9;
  border-radius: 6px;
  padding: 6px 8px;
}

.copy-button,
.close-button {
  border: 1px solid rgba(0, 0, 0, 0.2);
  background: #fff;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 13px;
}

.copy-button:hover,
.close-button:hover {
  background: #f3f4f6;
}

@media (max-width: 640px) {
  .credential-row {
    grid-template-columns: 1fr;
    gap: 6px;
  }
}
</style>
